<template>
  <div class="qrscanner-container">
    <div class="camera-container">
      <canvas class="camera" ref="cameraCanvas"></canvas>
    </div>
    <div class="body">
      <div class="close">
        <RouterLink :to="{ name: 'ApplicationsList' }">
          <FeatherIcon icon="x" stroke="white" />
        </RouterLink>
      </div>

      <div class="top-message content">
        <p class="text-size-18 text-weight-500 text-white">出退勤リーダー</p>
        <p class="mt-2 text-size-14 text-white">
          スキャンするにはQRコードをフレーム内に合わせてください
        </p>
      </div>

      <div ref="emptyCamerabox" class="highlight"></div>

      <div class="bottom">
        <div class="content">
          <p class="text-size-14 text-white">
            <template v-if="isBrowser">
              QRコードが読み取れない場合は事業所担当者に4桁のコードを確認して以下に入力してください
            </template>
            <template v-else>
              QRコードが読み取れない場合は
              <RouterLink
                class="link"
                :to="{
                  name: 'QrScanner',
                  query: $route.query
                }"
                >旧出退勤リーダー</RouterLink
              >
              を使うか、事業所担当者に4桁のコードを確認して以下に入力してください
            </template>
          </p>

          <AttendanceCodeForm :workStatus="workStatus" @submit="submit" />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { applicationService } from "@/app/services/ApplicationService";
import { systemService } from "@/app/services/SystemService";
import { showErrorModal } from "@/utils/showErrorModal";
import { applicationStore } from "@/store/application";
import { Component, Vue } from "vue-facing-decorator";
import jsQR from "jsqr";
import { deviceModule } from "@/store/device";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import AttendanceCodeForm from "@/components/organisms/orders/AttendanceCodeForm.vue";
import { openApplicationSettings } from "@/utils/diagnostic";
import { trainingService } from "@/app/services/TrainingService";
import { AbTest3300, AbTest3401 } from "@/app/services/AbTestService";

@Component({
  components: {
    FeatherIcon,
    AttendanceCodeForm
  }
})
export default class QrScannerWeb extends Vue {
  stream: MediaStream | null = null;

  get applicationId() {
    return Number(this.$route.query.id);
  }

  get workStatus() {
    return this.$route.query.workStatus;
  }

  get isOvertime() {
    return this.$route.query.isOvertime === "true";
  }

  get isBrowser() {
    return deviceModule.isBrowser;
  }

  mounted() {
    const query = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      route_name: (this.$route.name as string) || undefined,
      event: "view"
    };
    systemService.log(query);
    // QRスキャナーを起動
    this.startScan();
  }

  beforeUnmount() {
    // streamを停止
    this.stream?.getTracks().forEach(track => {
      track.stop();
    });
    this.stream = null;
  }

  async showErrorMessage(message: string) {
    await this.$bvModal.msgBoxOk(message, {
      centered: true
    });
  }

  async validateGetUserMedia() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      throw new Error(
        "カメラが見つかりません。事業所担当者に4桁のコードを確認して入力してください"
      );
    }

    const hasCameraDevice = (
      await navigator.mediaDevices.enumerateDevices()
    ).some(d => d.kind === "videoinput");
    if (!hasCameraDevice) {
      throw new Error(
        "カメラが見つかりません。事業所担当者に4桁のコードを確認して入力してください"
      );
    }

    if (!navigator.mediaDevices.getUserMedia) {
      throw new Error(
        "カメラが見つかりません。事業所担当者に4桁のコードを確認して入力するか、旧出退勤リーダーを使ってください"
      );
    }
  }

  async startScan() {
    await this.$nextTick();

    const video = document.createElement("video") as HTMLVideoElement;

    const options = {
      video: {
        facingMode: "environment"
      }
    };
    try {
      await this.validateGetUserMedia();
    } catch (e) {
      await this.showErrorMessage((e as Error).message);
      return;
    }

    try {
      this.stream = await navigator.mediaDevices.getUserMedia(options);
    } catch (e) {
      await this.notifyCameraIsNotAuthorized();
      return;
    }

    video.srcObject = this.stream;
    video.setAttribute("playsinline", "true");
    video.setAttribute("webkit-playsinline", "true");
    video.play();
    const cameraHighlightEl = this.$refs.emptyCamerabox as HTMLElement;
    if (!cameraHighlightEl) return;
    const rect = cameraHighlightEl.getBoundingClientRect();

    const tick = async () => {
      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        const canvasEl = this.$refs.cameraCanvas as HTMLCanvasElement | null;
        if (!canvasEl) return;
        const ctx = canvasEl.getContext("2d", {
          willReadFrequently: true
        })! as CanvasRenderingContext2D;

        canvasEl.height = video.videoHeight;
        canvasEl.width = video.videoWidth;

        const cw = canvasEl.width;
        const ch = canvasEl.height;
        const w = rect.width;
        // canvasは画面の大きさに合わせて拡大されるので、拡大率を計算する
        const zoom = canvasEl.getBoundingClientRect().width / video.videoWidth;
        ctx.drawImage(video, 0, 0, cw, ch);

        // darken the image with a 50% black fill
        ctx.save();
        ctx.globalAlpha = 0.5;
        ctx.fillStyle = "black";
        ctx.fillRect(0, 0, cw, ch);
        ctx.restore();

        ctx.save();
        ctx.beginPath();
        const highlightRect = {
          x: (cw - w / zoom) / 2,
          y: rect.y / zoom,
          width: w / zoom,
          height: w / zoom
        };
        // @ts-ignore
        if (ctx.roundRect) {
          // @ts-ignore
          ctx.roundRect(
            highlightRect.x,
            highlightRect.y,
            highlightRect.width,
            highlightRect.height,
            10
          );
        } else {
          // firefoxはroundRectが実装されていない
          ctx.rect(
            highlightRect.x,
            highlightRect.y,
            highlightRect.width,
            highlightRect.height
          );
        }
        ctx.clip();
        ctx.drawImage(video, 0, 0, cw, ch);
        ctx.restore();
        const imageData = ctx.getImageData(
          highlightRect.x,
          highlightRect.y,
          highlightRect.width,
          highlightRect.height
        );
        const code = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: "dontInvert"
        });

        if (code && code.data) {
          const isSuccess = await this.submit(code.data, "qrcode");
          if (isSuccess) return;
        }
      }
      if (this.stream) requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }

  async notifyCameraIsNotAuthorized() {
    if (this.isBrowser) {
      await this.$bvModal.msgBoxOk(
        "カメラの起動に失敗しました。カメラの起動を許可してください。",
        { centered: true }
      );
      return;
    }

    const isOpenApplicationSettings = await this.$bvModal.msgBoxConfirm(
      "カメラが利用できません。設定アプリからカメラのアクセスを許可し、アプリを再起動してください。",
      { centered: true, okTitle: "設定アプリを開く", cancelTitle: "キャンセル" }
    );

    if (isOpenApplicationSettings) {
      await openApplicationSettings();
    }
  }

  async submit(
    officeCode: string,
    method: "qrcode" | "manual"
  ): Promise<boolean> {
    // ログを記録する
    const query = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      route_name: (this.$route.name as string) || undefined,
      event: "submit",
      workStatus: this.workStatus,
      officeCode,
      method
    };
    systemService.log(query);

    try {
      this.$loading.show();
      const isStartWork = this.workStatus === "start";
      if (isStartWork) {
        const trainingId = await trainingService.searchNotFinishedTraining(
          this.applicationId
        );
        await applicationService.startWork(this.applicationId, officeCode);
        if (trainingId) {
          // トレーニングがある場合はトレーニングページに遷移
          await this.$router.replace({
            name: "TrainingDetail",
            params: { trainingId },
            query: { applicationId: this.applicationId.toString() }
          });
          return true;
        }
      } else {
        await applicationService.finishWork(
          this.applicationId,
          officeCode,
          this.isOvertime
        );
        AbTest3401.registerEvent(AbTest3401.eventTypes.completeWork);
        AbTest3300.registerEvent(AbTest3300.eventTypes.completeWork);
      }
      await applicationStore.fetchApplications();
      await this.$router.replace({
        name: this.workStatus === "start" ? "WorkStarted" : "WorkFinished",
        params: { applicationId: this.applicationId.toString() }
      });
      return true;
    } catch (error) {
      await showErrorModal(this, error as Error);
    } finally {
      this.$loading.hide();
    }
    return false;
  }
}
</script>

<style lang="scss" scoped>
.qrscanner-container {
  position: relative;
  max-width: 600px;
  min-height: 100vh;
  margin: auto;
  background: rgb(0 0 0 / 100%);

  .camera-container {
    position: absolute;
    top: 0;
    left: 0;
    max-width: 600px;
    height: 100dvh;

    canvas {
      width: 100%;
    }
  }

  .body {
    position: relative;
    margin: 0 auto;
    overflow: hidden;
    text-align: center;
  }

  .content {
    width: 240px;
    margin: 0 auto;
  }

  .close {
    position: absolute;
    top: calc(env(safe-area-inset-top, 0px) + 10px);
    right: 16px;
  }

  .top-message {
    padding-top: 50px;
    padding-bottom: 20px;
  }

  .highlight {
    width: 240px;
    height: 240px;
  }

  .bottom {
    padding-top: 0;
    margin-top: 50px;
    background: black;
    box-shadow: 0 -15px 15px 10px black;
  }

  .link {
    color: $primary-yellow;
    text-decoration: underline;
  }
}
</style>
